import React, { useEffect, useState, useMemo, useCallback } from "react";
import { cloneDeep, compact, isEmpty } from "lodash";
import { Row, Col, Typography, Radio, Button, Popover, Tooltip, Alert, Badge } from "antd";
import { useHistory } from 'react-router-dom';
import { PlusOutlined } from "@ant-design/icons";
import { DeleteIcon } from 'components/Icons';

import { SortIcon, CloseIcon  } from "./Icons";
import { BASE_GUTTER, MODAL_SIZE_SMALL } from "constants/Layout";
import CustomSelect from 'components/CustomSelect';
import useUrlSearchParams from "hooks/useUrlSearchParams";
import { MULTI_SORT_COLUMNS_LIMIT } from 'views/Findings/Constants'
import { updateURLSearch } from "utils/urlUtil";

const DefaultRow = { property: '', direction: 'ascend'}

const MultiSorting = ({ activeColumns, children }) => {
  const history = useHistory();
  const [selectedColumns, setSelectedColumns] = useState([])
  const [options, setOptions] = useState([])
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState(false);
  const params = useUrlSearchParams();
  const {pagination: { sort }, ...restParams} = useMemo(() => params, [params]);
  const [count , setCount] = useState(0);

  useEffect(() => {
    if (!visible && error) {
      const selectedColumns = parseMultiSort(sort);
      setSelectedColumns(isEmpty(selectedColumns) ? [DefaultRow] : selectedColumns);
      setCount(selectedColumns.length);
      setError();
    }
  }, [visible, error, sort])

  useEffect(() => {
    let count = 0;
    if (sort) {
      const selectedColumns = parseMultiSort(sort);
      count = selectedColumns.length;
      setSelectedColumns(selectedColumns);
    } else {
      setSelectedColumns([DefaultRow]);
    }
    setCount(count);
  }, [sort, setSelectedColumns])

  useEffect(() => {
    const selectedFields = selectedColumns.map(s => s.property);
    const options = activeColumns.map(activeCol => {
      return {...activeCol, label: activeCol.header.replace(/\s+/g, ' '), value: activeCol.field, disabled: selectedFields.includes(activeCol.field)}
    })
    setOptions(options)
  }, [activeColumns, selectedColumns])

  const addRow = useCallback(() => {
    const columns = cloneDeep(selectedColumns)
    columns.push(DefaultRow)
    setSelectedColumns(columns)
  }, [selectedColumns])

  const removeRow = useCallback(index => {
    const columns = cloneDeep(selectedColumns)
    columns.splice(index, 1)
    if (error) {
      setError(columns.some(col => isEmpty(col.property)))
    }
    setSelectedColumns(columns)
  }, [selectedColumns, error])

  
  const handleOnChange = useCallback((value, index) => {
    const columns = cloneDeep(selectedColumns)
    columns[index].property = value;
    setSelectedColumns(columns)
  }, [selectedColumns])
  
  const onDirectionChange = useCallback((e, index) => {
    const columns = cloneDeep(selectedColumns)
    columns[index].direction = e.target.value;
    setSelectedColumns(columns)
  }, [selectedColumns])

  const handleApply = useCallback(() => {
    const { pagination={} } = params;
    let str = '', hasError = false, counter = 0;
    selectedColumns.forEach((col, i) => {
      if (!isEmpty(col.property) && !isEmpty(col.direction)) {
        str += `${col.property},${col.direction}${selectedColumns.length - 1 !== i ? ':' : ''}`;
        counter += 1;
      } else {
        hasError = true;
      }
    })
    if (hasError) {
      setError(true);
      return;
    }

    setVisible(false);
    setError(false);
    if (str) {
      setCount(counter);
      updateURLSearch(history, {pagination: {...pagination, sort: str}, ...restParams})
    }

  }, [history, params, selectedColumns, restParams])

  const resetSorting = useCallback(() => {
    const { pagination={}} = params;
    setCount();
    updateURLSearch(history, {pagination: {...pagination, sort: ''}, ...restParams})
  }, [history, params, restParams])

  const popoverContentProps = {
    selectedColumns,
    removeRow,
    handleOnChange,
    onDirectionChange,
    handleApply,
    options,
    addRow,
    error,
  }

  if(children){
    return (
      <Popover
        trigger={["click"]}
        content={<PopoverContent {...popoverContentProps} />}
        placement="bottomLeft"
        visible={visible}
        onVisibleChange={setVisible}
      >
        {children}
      </Popover>
    );
  }

  return (
    <div>
      <Popover trigger={["click"]} content={<PopoverContent {...popoverContentProps}/>} placement="bottomLeft" visible={visible} onVisibleChange={setVisible}>
          <Tooltip title="Sort multiple columns" placement="topRight">
              <Button id='sort-by' type="link" className=" p-x-0"> <SortIcon />&nbsp;Sort By&nbsp;<Badge size="small" count={count} className="no-border" style={{backgroundColor: '#35198e', marginBottom: 3}} /></Button>
          </Tooltip>
      </Popover>
      { count > 0 && <Tooltip title="Reset multiple columns sorting" placement="topRight">
          <Button id='reset-sorting' type="link" onClick={resetSorting} className="p-0"> <CloseIcon/> </Button>
        </Tooltip>}
    </div>
  )
}

const PopoverContent = ({selectedColumns, handleApply, addRow, ...restProps}) => {
  const limit = selectedColumns.length === MULTI_SORT_COLUMNS_LIMIT;
  return (
    <div style={{ width: MODAL_SIZE_SMALL }}>
      <Row gutter={BASE_GUTTER}>
        <Col span={24}>
          <Typography.Text strong>Sort records </Typography.Text><br />
          <Typography.Text strong>Note:</Typography.Text> Multiple sorting is limited to {MULTI_SORT_COLUMNS_LIMIT} columns.
        </Col>
      </Row>
      <Row  gutter={BASE_GUTTER} align="middle">
        {
          selectedColumns.map((selected, i) => <SelectedItem key={i} {...{selectedColumns, selected, index: i, ...restProps}}/>)
        }
        {restProps.error && <Col span={24}><Alert message={"Some fields are empty."} type="error" showIcon/></Col>}
        <Col span={2} />
        <Col span={10}>
          <Button block type="dashed" disabled={limit} size="small" onClick={() => addRow()} ><PlusOutlined /></Button>
        </Col>
        <Col span={12}>
          <Button type="primary" className="pull-right" size="small" onClick={handleApply}>
            Apply
          </Button>
        </Col>
      </Row>
    </div>
  )
}

const SelectedItem = ({selectedColumns, selected, options, handleOnChange, onDirectionChange, removeRow, index }) => {
  return (
    <>
      <Col span={2} className="text-right">{index === 0 && 'By'}</Col>
      <Col  span={10}>
        <CustomSelect
          showSearch
          value={selected.property}
          options={options}
          className='w-100'
          onChange={value => handleOnChange(value, index)}
          optionFilterProp='label'
        />
      </Col>
      <Col span={9}>
        <Radio.Group onChange={e => onDirectionChange(e, index)} value={selected.direction}>
          <Radio value={'ascend'}>Asc</Radio>
          <Radio value={'descend'}>Desc</Radio>
        </Radio.Group>
      </Col>
      <Col span={3}>
        { selectedColumns.length - 1 > 0 && <Tooltip title="Remove field from sort records." placement="topRight"><DeleteIcon className="clickable danger" onClick={() => removeRow(index)}/></Tooltip> }
      </Col>
    </>
  )
}

export default MultiSorting;

export const parseMultiSort = (sortColumns) => {
  const _sortColumns = compact(sortColumns?.split(':'));
  return _sortColumns.map(column => {
      const [property, direction] = column.split(',');
      return { property, direction }
  })
}